import * as React from 'react'

import { i18n } from '@thg-commerce/enterprise-core'
import { Image } from '@thg-commerce/gravity-elements/'
import { ImageProps } from '@thg-commerce/gravity-elements/Image/Image'

import {
  StyledGrid,
  ImageGridItem,
  MethodGridItem,
  Title,
  Description,
  StyledHeading,
  StyledText,
  IngredientList,
  IngredientItem,
  StyledButton,
  MethodItemNumber,
  MethodItemText,
  NutritionalInformationTable,
  NutritionalInformationRow,
  NutritionalInformationHeader,
  NutritionalInformationCell,
} from './styles'

interface RecipeLinkElementNode {
  node: 'element'
  type: 'link'
  props?: {
    text?: string
    uri: string
    target?: 'blank' | 'self' | 'parent' | 'top'
    rel?: string
    id?: string
  }
}

interface IngredientProps {
  quantity: string
  unit?: string
  ingredient: string | RecipeLinkElementNode
  notes?: string
}

interface InstructionProps {
  instruction: string
  image?: Omit<ImageProps, 'isAmp'>
}

interface NutritionalInformationProps {
  calories?: number
  caloriesFromFat?: number
  totalFat?: number
  saturatedFat?: number
  transFat?: number
  polyunsaturatedFat?: number
  monounsaturatedFat?: number
  cholesterol?: number
  sodium?: number
  potassium?: number
  totalCarbohydrates?: number
  dietaryFiber?: number
  sugar?: number
  protein?: number
  vitaminA?: number
  vitaminC?: number
  calcium?: number
  iron?: number
}

export interface RecipeProps {
  title?: string
  description?: string
  servings?: string
  prepTime?: string
  cookTime?: string
  passiveTime?: string
  ingredients?: IngredientProps[]
  instructions?: InstructionProps[]
  nutritionalInformation?: NutritionalInformationProps
  isAmp: boolean
}

export const Recipe = React.memo(
  ({
    title,
    description,
    servings,
    prepTime,
    cookTime,
    passiveTime,
    ingredients,
    instructions,
    nutritionalInformation,
    isAmp,
  }: RecipeProps) => {
    const i18nRecipeText = {
      servings: i18n('widget.recipe.servings'),
      prepTime: i18n('widget.recipe.prep.time'),
      cookTime: i18n('widget.recipe.cook.time'),
      passiveTime: i18n('widget.recipe.passive.time'),
      ingredients: i18n('widget.recipe.ingredients'),
      instructions: i18n('widget.recipe.instructions'),
      nutritionalInfo: i18n('widget.recipe.nutritional.info'),
      calories: i18n('widget.recipe.calories'),
      totalFat: i18n('widget.recipe.total.fat'),
      saturatedFat: i18n('widget.recipe.saturated.fat'),
      transFat: i18n('widget.recipe.trans.fat'),
      polyunsaturatedFat: i18n('widget.recipe.polyunsaturated.fat'),
      monounsaturatedFat: i18n('widget.recipe.monounsaturated.fat'),
      cholesterol: i18n('widget.recipe.cholesterol'),
      sodium: i18n('widget.recipe.sodium'),
      potassium: i18n('widget.recipe.potassium'),
      totalCarbohydrates: i18n('widget.recipe.total.carbohydrates'),
      dietaryFiber: i18n('widget.recipe.dietary.fiber'),
      sugar: i18n('widget.recipe.sugar'),
      protein: i18n('widget.recipe.protein'),
      vitaminA: i18n('widget.recipe.vitamin.a'),
      vitaminC: i18n('widget.recipe.vitamin.c'),
      calcium: i18n('widget.recipe.calcium'),
      iron: i18n('widget.recipe.iron'),
      dailyValue: i18n('widget.recipe.daily.value'),
    }

    const ingredientItems = ingredients?.map((item, index) => {
      return (
        <IngredientItem key={`ingredient-item-${index}`}>
          {item.quantity} {item.unit}{' '}
          {typeof item.ingredient === 'object' ? (
            <StyledButton
              renderedAs="a"
              href={item.ingredient?.props?.uri}
              emphasis="low"
            >
              <span>{item.ingredient?.props?.text}</span>
            </StyledButton>
          ) : (
            item.ingredient
          )}{' '}
          {item.notes}
        </IngredientItem>
      )
    })

    const instructionItems = instructions?.map((item, index) => {
      const doubleIndex = index * 2

      if (item?.image?.src[0]?.url) {
        return (
          <React.Fragment key={`instruction-item-${index}`}>
            <ImageGridItem
              colSpan={[12, 12, 6, 4]}
              colStart={1}
              rowStart={doubleIndex + 1}
            >
              <Image
                src={item.image.src}
                width="100%"
                height="auto"
                alt={item.image.alt || ''}
                isAmp={isAmp}
              />
            </ImageGridItem>
            <MethodGridItem
              colSpan={[12, 12, 6, 8]}
              colStart={[1, 1, 7, 5]}
              rowStart={[
                doubleIndex + 2,
                doubleIndex + 2,
                doubleIndex + 1,
                doubleIndex + 1,
              ]}
            >
              <MethodItemNumber>{index + 1}.</MethodItemNumber>
              <MethodItemText
                __dangerouslyAllowedTags={['strong', 'em']}
                content={item.instruction}
              />
            </MethodGridItem>
          </React.Fragment>
        )
      }
      return (
        <MethodGridItem
          colSpan={12}
          colStart={[1, 1, 1, 1]}
          rowStart={[
            doubleIndex + 2,
            doubleIndex + 2,
            doubleIndex + 1,
            doubleIndex + 1,
          ]}
          key={`instruction-item-${index}`}
        >
          <MethodItemNumber>{index + 1}.</MethodItemNumber>
          <MethodItemText
            __dangerouslyAllowedTags={['strong', 'em']}
            content={item.instruction}
          />
        </MethodGridItem>
      )
    })

    const getNutritionalUnits = (nutritionalInformationKey: string) => {
      switch (nutritionalInformationKey) {
        case 'calories':
          return [i18nRecipeText.calories, '']
        case 'totalFat':
          return [i18nRecipeText.totalFat, 'g']
        case 'saturatedFat':
          return [i18nRecipeText.saturatedFat, 'g']
        case 'transFat':
          return [i18nRecipeText.transFat, 'g']
        case 'polyunsaturatedFat':
          return [i18nRecipeText.polyunsaturatedFat, 'g']
        case 'monounsaturatedFat':
          return [i18nRecipeText.monounsaturatedFat, 'g']
        case 'cholesterol':
          return [i18nRecipeText.cholesterol, 'mg']
        case 'sodium':
          return [i18nRecipeText.sodium, 'mg']
        case 'potassium':
          return [i18nRecipeText.potassium, 'mg']
        case 'totalCarbohydrates':
          return [i18nRecipeText.totalCarbohydrates, 'g']
        case 'dietaryFiber':
          return [i18nRecipeText.dietaryFiber, 'g']
        case 'sugar':
          return [i18nRecipeText.sugar, 'g']
        case 'protein':
          return [i18nRecipeText.protein, 'g']
        case 'vitaminA':
          return [i18nRecipeText.vitaminA, i18nRecipeText.dailyValue]
        case 'vitaminC':
          return [i18nRecipeText.vitaminC, i18nRecipeText.dailyValue]
        case 'calcium':
          return [i18nRecipeText.calcium, i18nRecipeText.dailyValue]
        case 'iron':
          return [i18nRecipeText.iron, i18nRecipeText.dailyValue]
        default:
          return []
      }
    }

    const nutritionalInformationItems =
      nutritionalInformation &&
      Object.entries(nutritionalInformation).map(
        ([nutritionalInformationKey, nutritionalInformationValue], index) => {
          if (nutritionalInformationValue !== 0) {
            return (
              <NutritionalInformationRow
                key={`nutritional-information-${index}`}
              >
                <NutritionalInformationHeader scope="row">
                  {getNutritionalUnits(nutritionalInformationKey)[0]}
                </NutritionalInformationHeader>
                <NutritionalInformationCell>
                  {nutritionalInformationValue}
                  {getNutritionalUnits(nutritionalInformationKey)[1]}
                </NutritionalInformationCell>
              </NutritionalInformationRow>
            )
          }
          return
        },
      )

    const isEmptyNutritionValue =
      nutritionalInformation &&
      !Object.values(nutritionalInformation).some(
        (nutritionalInformationValue) => nutritionalInformationValue !== 0,
      )

    return (
      <React.Fragment>
        {title && <Title data-testid="title"> {title} </Title>}
        {description && <Description content={description} />}
        {(servings || prepTime || cookTime || passiveTime) && (
          <dl>
            {servings && (
              <React.Fragment>
                <StyledHeading data-testid="servings">
                  {i18nRecipeText.servings}
                </StyledHeading>
                <StyledText data-testid="servings">{servings}</StyledText>
              </React.Fragment>
            )}
            {prepTime && (
              <React.Fragment>
                <StyledHeading data-testid="prep-time">
                  {i18nRecipeText.prepTime}
                </StyledHeading>
                <StyledText data-testid="prep-time">{prepTime}</StyledText>
              </React.Fragment>
            )}
            {cookTime && (
              <React.Fragment>
                <StyledHeading data-testid="cook-time">
                  {i18nRecipeText.cookTime}
                </StyledHeading>
                <StyledText data-testid="cook-time">{cookTime}</StyledText>
              </React.Fragment>
            )}
            {passiveTime && (
              <React.Fragment>
                <StyledHeading data-testid="passive-time">
                  {i18nRecipeText.passiveTime}
                </StyledHeading>
                <StyledText data-testid="passive-time">
                  {passiveTime}
                </StyledText>
              </React.Fragment>
            )}
          </dl>
        )}
        {ingredients && ingredients.length !== 0 && (
          <React.Fragment>
            <Title data-testid="ingredients">
              {i18nRecipeText.ingredients}
            </Title>
            <IngredientList data-testid="ingredients">
              {ingredientItems}
            </IngredientList>
          </React.Fragment>
        )}
        {instructions && instructions.length !== 0 && (
          <React.Fragment>
            <Title data-testid="instructions">
              {i18nRecipeText.instructions}
            </Title>
            <StyledGrid data-testid="instructions">
              {instructionItems}
            </StyledGrid>
          </React.Fragment>
        )}
        {nutritionalInformation && !isEmptyNutritionValue && (
          <React.Fragment>
            <Title data-testid="nutrition">
              {i18nRecipeText.nutritionalInfo}
            </Title>
            <NutritionalInformationTable data-testid="nutrition">
              <tbody>{nutritionalInformationItems}</tbody>
            </NutritionalInformationTable>
          </React.Fragment>
        )}
      </React.Fragment>
    )
  },
)
